import { combineLatest, Observable, of, Subject } from 'rxjs';
import { Component, OnDestroy } from '@angular/core';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

import { ShopKeys } from '@config/keys/shop.keys';
import { ProductKeys } from '@config/keys/product.keys';

import { IZerosixAsset } from '@core/models/zerosix.model';
import { DoodProductModel } from '@core/models/product.model';

import { CartService } from '@core/services/cart/cart.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';

import { ContentStoreDispatcher } from '@common/dispatchers/content.dispatcher';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { ProductAddModalComponent } from '@shared/modals/product-add-modal/product-add-modal.component';

export type ZerosixOfferedProductPickerModalInputs = {
  asset?: IZerosixAsset;
};

export interface ZerosixOfferedProductPickerModalOutputs {
  type: 'productSelected';
}

@Component({
  selector: 'app-zerosix-offered-product-picker-modal',
  templateUrl: './zerosix-offered-product-picker-modal.component.html',
})
export class ZerosixOfferedProductPickerModalComponent
  extends ModalScrollBlockBase
  implements OnDestroy
{
  private _destroyerRef = new Subject<boolean>();

  static handle = 'zerosix-offered-product-picker-modal';

  index$: Observable<number> = this.modalsService
    .getData(ZerosixOfferedProductPickerModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 6)));

  modalData$ = this.modalsService.getData(ZerosixOfferedProductPickerModalComponent.handle);

  protected readonly shopIdFromCart$: Observable<undefined | string> =
    this.cartSelector.selectActive.pipe(map(cart => cart?.shop));

  products$: Observable<DoodProductModel[]> = combineLatest({
    shop: this.shopIdFromCart$.pipe(
      switchMap(shopId => (shopId ? this.shopSelector.selectShop(shopId) : of(undefined))),
    ),
    modalData: this.modalData$,
  }).pipe(
    map(({ shop, modalData }) => {
      if (!shop) {
        return [];
      }

      return shop[ShopKeys.Products].filter(p => {
        const externalId = p[ProductKeys.ExternalId];
        if (externalId) {
          return modalData?.data?.asset?.external_ids?.includes(externalId);
        }
        return false;
      });
    }),
  );

  constructor(
    private cartSelector: CartStoreSelector,
    private shopSelector: ShopStoreSelector,
    private readonly cartService: CartService,
    private readonly modalsService: ModalsService<
      ZerosixOfferedProductPickerModalInputs,
      ZerosixOfferedProductPickerModalOutputs
    >,
    private readonly zerosixService: ZerosixService,
    private contentDispatcher: ContentStoreDispatcher,
  ) {
    super();
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  close(): void {
    this.modalsService.close(ZerosixOfferedProductPickerModalComponent.handle);
  }

  onNavigateTo(product: DoodProductModel, shopId: string | null | undefined): void {
    if (!shopId) {
      return;
    }

    this.contentDispatcher.updateProduct({
      productId: product.id,
      shopIsNotActive: false,
      shopId: product.shop_id ?? shopId,
    });
    ProductAddModalComponent.allowAddToCart = true;
    ProductAddModalComponent.onAdd = (): void => {
      this.close();
      this.modalsService.emitEvent({
        data: { type: 'productSelected' },
        handle: ZerosixOfferedProductPickerModalComponent.handle,
      });
      ProductAddModalComponent.onAdd = (): void => {};
    };
    this.modalsService.open(ProductAddModalComponent.handle);
  }

  addVoucherWithoutProduct(): void {
    this.close();
    this.modalsService.emitEvent({
      data: { type: 'productSelected' },
      handle: ZerosixOfferedProductPickerModalComponent.handle,
    });
  }

  cancel(): void {
    this.modalData$.pipe(take(1), takeUntil(this._destroyerRef)).subscribe(modalData => {
      const asset = modalData?.data?.asset;
      if (!asset) {
        this.close();
        return;
      }
      const coupon = this.zerosixService.calculateCouponFromAsset(asset);
      if (!coupon) {
        return;
      }
      this.cartService.removeCoupon(coupon);
      this.close();
    });
  }
}
